import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { aender, lies, schick, weg } from "../../api/client";
import type { Mitglied, Projekt, Projektrolle } from "../../api/types";
import { Feld, Meldung } from "../../components/Bausteine";
import { Auswahl } from "../../components/Auswahl";

const ROLLE: Record<Projektrolle, string> = {
  leitung: "Leitung – freigeben, abnehmen",
  bearbeitung: "Bearbeitung – schreiben, erfassen",
  lesen: "Lesen – nur Freigegebenes",
};

/** Wer sich anmelden darf (E5/E6). Externe bekommen in der Regel „Lesen“. */
export function TeamReiter({ projekt }: { projekt: Projekt }) {
  const qc = useQueryClient();
  const liste = useQuery({
    queryKey: ["mitglieder", projekt.id],
    queryFn: () => lies<Mitglied[]>(`/projekte/${projekt.id}/mitglieder`),
  });
  const neu = () => qc.invalidateQueries({ queryKey: ["mitglieder", projekt.id] });
  const [f, setF] = useState<{ email: string; name: string; rolle: Projektrolle; mail: boolean }>(
    { email: "", name: "", rolle: "lesen", mail: true });
  // Was mit der Einladungsmail passiert ist (SPEC §11.1): verschickt, nicht gewollt, oder
  // gescheitert — dann muss die Leitung den Link selbst schicken, und soll das wissen.
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const einladen = useMutation({
    mutationFn: () => schick<{ mail: string | null }>(`/projekte/${projekt.id}/mitglieder`, f),
    onSuccess: (r) => {
      setErgebnis(r.mail === "verschickt" ? `${f.email} ist eingeladen; die Einladung mit Link ist verschickt.`
        : r.mail ? `${f.email} ist eingeladen, aber die Mail ging nicht hinaus (${r.mail}). Bitte den Link selbst schicken: ${location.origin}/p/${projekt.kurz}`
          : `${f.email} ist eingeladen. Den Link schicken Sie selbst: ${location.origin}/p/${projekt.kurz}`);
      setF({ email: "", name: "", rolle: "lesen", mail: true });
      neu();
    },
  });
  const rolle = useMutation({
    mutationFn: ({ id, r }: { id: string; r: Projektrolle }) => aender(`/projekte/${projekt.id}/mitglieder/${id}`, { rolle: r }),
    onSuccess: neu,
  });
  const raus = useMutation({ mutationFn: (id: string) => weg(`/projekte/${projekt.id}/mitglieder/${id}`), onSuccess: neu });

  return (
    <>
      <section className="karte">
        <div className="karte-kopf"><h2>Zugang zum Projekt</h2></div>
        <Meldung fehler={liste.error ?? rolle.error ?? raus.error} />
        <div className="liste">
          {liste.data?.map((m) => (
            <div key={m.id} className="zeile">
              <span className={`chip ${m.hausrolle === "extern" ? "" : "akzent"}`}>{m.hausrolle === "extern" ? "extern" : "Büro"}</span>
              <div className="haupt">
                <div>{m.anzeigename || m.email}</div>
                <div className="mute">{m.email}{m.firma && ` · ${m.firma}`}{!m.angemeldet && " · noch nie angemeldet"}</div>
              </div>
              <span style={{ display: "flex", gap: 6 }}>
                <Auswahl style={{ width: "auto" }} value={m.rolle}
                        onChange={(e) => rolle.mutate({ id: m.id, r: e.target.value as Projektrolle })}>
                  {(Object.keys(ROLLE) as Projektrolle[])
                    .filter((r) => r !== "leitung" || m.hausrolle !== "extern")
                    .map((r) => <option key={r} value={r}>{ROLLE[r].split(" –")[0]}</option>)}
                </Auswahl>
                <button className="knopf leise gefahr" onClick={() => confirm(`${m.email} den Zugang zum Projekt nehmen?`) && raus.mutate(m.id)}>Entfernen</button>
              </span>
            </div>
          ))}
        </div>
      </section>
      <section className="karte">
        <div className="karte-kopf"><h2>Einladen</h2></div>
        <div className="raster">
          <Feld titel="E-Mail"><input type="email" className="eingabe" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Feld>
          <Feld titel="Name"><input className="eingabe" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Feld>
          <Feld titel="Recht">
            <Auswahl value={f.rolle} onChange={(e) => setF({ ...f, rolle: e.target.value as Projektrolle })}>
              {(Object.keys(ROLLE) as Projektrolle[]).map((r) => <option key={r} value={r}>{ROLLE[r]}</option>)}
            </Auswahl>
          </Feld>
        </div>
        <label className="haken">
          <input type="checkbox" checked={f.mail} onChange={(e) => setF({ ...f, mail: e.target.checked })} />
          Einladung mit Link per Mail schicken
        </label>
        <p className="mute">Die Person meldet sich danach unter dieser Adresse mit einem Code an – ohne Passwort.</p>
        {ergebnis && <div className={ergebnis.includes("nicht hinaus") ? "hinweis" : "mute"}>{ergebnis}</div>}
        <Meldung fehler={einladen.error} />
        <button className="knopf haupt" disabled={!f.email.includes("@") || einladen.isPending} onClick={() => einladen.mutate()}>Einladen</button>
      </section>
    </>
  );
}
